import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';

import Header from 'components/Common/Header';
import Tab from 'components/Common/Tab';
import TextInput from 'components/Common/TextInput';
import Table from 'components/Common/StyledTable';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { Card, IconButton } from '@mui/material';
import routes from 'constants/routes';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, store } from 'redux/store';
import { getStaffList, SetStatus } from 'redux/slices/staff/staffSlice';
import MuiLoader from 'components/Common/Loader';
import { updateTableState } from 'redux/slices/common/appSlice';

const StyledContainer = styled.div({
    backgroundColor: '#F2F2F6',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
});

const StyledActionContainer = styled.div({
    marginBottom: 16,
    padding: 22,
});

const StyledInnerContainer = styled.div({
    backgroundColor: 'white',
    padding: 1,
    marginTop: 10,
});

const columns = [
    { id: 'name', label: 'Name' },
    { id: 'username', label: 'Username' },
    { id: 'title', label: 'Access Level' },
];

const Staff: React.FC = () => {
    const dispatch = useDispatch<typeof store.dispatch>();
    const navigate = useNavigate();
    const [tab, setTab] = useState<SetStatus>('ACTIVE');
    const [searchValue, setSearchValue] = useState('');
    const { staff, tableState } = useSelector((state: RootState) => ({
        staff: state.staff?.staffList,
        tableState: state.app.table,
    }));
    const { activeRecords, inactiveRecords, pendingRecords, deletedRecords, loading } = staff || {};
    const staffList = useMemo(() => {
        switch (tab) {
            case 'ACTIVE':
                return activeRecords;
            case 'INACTIVE':
                return inactiveRecords;
            case 'PENDING':
                return pendingRecords;
            case 'DELETED':
                return deletedRecords;
            default:
                return activeRecords;
        }
    }, [tab, activeRecords, inactiveRecords, pendingRecords, deletedRecords]);

    const setTabValue = (value: SetStatus) => {
        setTab(value);
    };
    useEffect(() => {
        const setStatus = tab;
        dispatch(getStaffList({ setStatus }));
    }, [tab, dispatch]);

    const getSearchData = (data: any, value: string) => {
        return data.filter((item: any) => item.name.toLowerCase().includes(value.toLowerCase()));
    };

    const getData = useMemo(() => {
        let data = staffList;
        if (searchValue) {
            dispatch(updateTableState({ ...tableState, pageNumber: 0 }));
            data = getSearchData(staffList, searchValue);
        }
        return data;
    }, [staffList, searchValue, dispatch]); //eslint-disable-line

    const navigationHandler = (row: any) => {
        navigate(routes.user.userDetail.replace(':username', row.username), { state: { type: 'employee', status: tab } });
    };

    return (
        <StyledContainer style={{ borderRadius: 12 }}>
            <MuiLoader loading={loading} absolute />
            <div style={{ padding: 22 }}>
                <Header />
            </div>
            <StyledActionContainer>
                <StyledInnerContainer>
                    <Card sx={{ mb: 0.5 }}>
                        <div style={{ padding: 16 }}>
                            <TextInput value={searchValue} onChange={(e: any) => setSearchValue(e.target.value)} />
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                paddingLeft: 6,
                                flexDirection: 'row',
                                justifyContent: 'space-around',
                            }}
                        >
                            <Tab
                                tabs={[
                                    { value: 'ACTIVE', label: 'Active Staff' },
                                    { value: 'DELETED', label: 'Inactive' },
                                    // { value: 'PENDING', label: 'Pending' },
                                    // { value: 'DELETED', label: 'Suspended' },
                                ]}
                                tabValue={tab}
                                setTabValue={setTabValue}
                            />

                            <IconButton sx={{ marginRight: 1 }} onClick={() => navigate(routes.staff.add)}>
                                <AddIcon htmlColor="black" />
                            </IconButton>
                        </div>
                    </Card>
                    <Table
                        unStyled
                        navigationHandler={navigationHandler}
                        columns={columns}
                        data={getData}
                        totalCount={getData?.length}
                        loading={loading}
                    />
                </StyledInnerContainer>
            </StyledActionContainer>
        </StyledContainer>
    );
};

export default Staff;
